import { z } from 'zod';

/**
 * Provider outcomes of a push (BB-72, PUSH-009): the class decides what the sender does, the code goes into facts
 * (`failed{code}`, `attrs.class`) and the raw provider reason is kept next to it for diagnostics. The provider tables
 * that map FCM / APNs answers onto these live in `@bb/integrations` (push classification).
 *
 * - `accepted` — the provider took it (`accepted_by_provider`; not `delivered`, which only an SDK receipt proves).
 * - `token_invalid` — the token is dead or wrong; mark it invalid (BB-31) and never retry (a bounce).
 * - `permanent_message` — this message can never be sent as is; no retry.
 * - `credential_error` — our credential was refused; refresh the token / JWT and retry once, then the credential is
 *   failing and the delivery backs off until it expires.
 * - `retryable` — try again later (`retryAfterMs` first, then the sender's backoff).
 * - `unknown` — the request may have reached the provider (timeout or drop after the write); retry only where a
 *   duplicate is harmless (Android dedupes by `did`, iOS replaces by `collapseKey`).
 */
export const PUSH_PROVIDERS = ['fcm', 'apns'] as const;
export type PushProvider = (typeof PUSH_PROVIDERS)[number];

export const PUSH_OUTCOME_CLASSES = ['accepted', 'token_invalid', 'permanent_message', 'credential_error', 'retryable', 'unknown'] as const;
export const PushOutcomeClass = z.enum(PUSH_OUTCOME_CLASSES);
export type PushOutcomeClass = z.infer<typeof PushOutcomeClass>;

export const PUSH_OUTCOME_CODES = [
  'accepted',
  // before the provider (wire builder, sender)
  'payload_too_large',
  'ttl_expired',
  'sending_disabled',
  'credential_invalid',
  // token_invalid
  'token_unregistered',
  'token_invalid',
  'token_not_for_topic',
  'sender_id_mismatch',
  // permanent_message
  'message_invalid',
  'provider_rejected',
  // credential_error
  'credential_rejected',
  'credential_expired',
  // retryable
  'rate_limited',
  'provider_unavailable',
  'connection_failed',
  // unknown
  'provider_outcome_unknown',
] as const;
export const PushOutcomeCode = z.enum(PUSH_OUTCOME_CODES);
export type PushOutcomeCode = z.infer<typeof PushOutcomeCode>;

/** Where a failure happened: before any push request (`pre_send`) or at the provider. */
export const PUSH_FAILURE_STAGES = ['pre_send', 'provider'] as const;
export type PushFailureStage = (typeof PUSH_FAILURE_STAGES)[number];

/** `markPushTokenInvalid` reasons of BB-31. */
export const TOKEN_INVALIDATION_REASONS = ['unregistered', 'invalid', 'environment_mismatch'] as const;
export type TokenInvalidationReason = (typeof TOKEN_INVALIDATION_REASONS)[number];

/**
 * Extra signals next to an outcome: `apns_environment_suspected` — `BadDeviceToken` for an app with both APNs
 * environments; `token_reregistered_after_invalidation` — APNs said `Unregistered` before the token was last
 * registered, so the token is kept; `provider_token_refresh_throttled` — APNs refused a too frequent JWT refresh.
 */
export const PUSH_DIAGNOSTICS = ['apns_environment_suspected', 'token_reregistered_after_invalidation', 'provider_token_refresh_throttled'] as const;
export type PushDiagnostic = (typeof PUSH_DIAGNOSTICS)[number];
